import { LE } from "@/constants/enums";
import { murnAddressFromFormkit, murnAddressToFormkit } from "@modernary/mui/components/fk";
import {
  fillLookupValuesByPropertyNames,
  fillLookupsForListOfMurns,
  fillPrefixedAddressLookupValues,
} from "@modernary/mui/components/lookup";
import {
  combineSeperatedDustControlFields,
  combineSeperatedVacantLandFields,
  removeExtraDustInspectionFields,
  removeExtraVLInspectionFields,
} from "../inspections/helpers";
import type { ComplaintInvestigation, ComplaintInvestigationNew } from "../inspections/inspection";

export default {
  addAuthRequestFields,
  getInitialData,
  skinnyResponseAdapter,
  responseAdapter,
  requestAdapter,
  fromFormkit,
  toFormkit,
};

function addAuthRequestFields(entity: ComplaintInvestigation) {
  entity._authRequestFields = [
    "id",
    "statusMurn",
    "programTypeMurn",
    "actionTakenMurn",
    "vacantLandInspectionSectionMurn",
  ];
  return entity;
}
function getInitialData() {
  const result = {} as ComplaintInvestigation; //TODO: ts
  return addAuthRequestFields(result);
}

/** Be sure to await this when using it. Commonly used for lists and child lists */
async function skinnyResponseAdapter(response: { data: ComplaintInvestigation }) {
  addAuthRequestFields(response.data);
  if (!response.data.assignedToObj) response.data.assignedToObj = {};
  if (response.data.assignedTo) {
    response.data.assignedToObj = JSON.parse(response.data.assignedTo);
  }

  if (!response.data.reviewedByObj) response.data.reviewedByObj = {};
  if (response.data.reviewedBy) {
    response.data.reviewedByObj = JSON.parse(response.data.reviewedBy);
  }

  if (response.data.complaints && response.data.complaints.length > 0) {
    const complaintIds: number[] = [];
    response.data.complaints.forEach((complaint) => {
      complaintIds.push(complaint.id);
    });
    response.data.complaintIds = complaintIds;
  }

  if (response.data?.dustControlInspection)
    await fillLookupValuesByPropertyNames(response.data.dustControlInspection, ["measurementUnitsMurn"]);

  if (response.data?.vacantLandInspection)
    await fillPrefixedAddressLookupValues(response.data.vacantLandInspection, ["mail"]);

  await fillLookupValuesByPropertyNames(response.data, [
    "typeMurn",
    "statusMurn",
    "programTypeMurn",
    "actionTakenMurn",
  ]);

  return response.data;
}

/** Be sure to await this when using it. */
async function responseAdapter(response: { data: ComplaintInvestigation }) {
  await skinnyResponseAdapter(response);

  if (response.data?.vacantLandInspection) {
    await fillLookupValuesByPropertyNames(response.data.vacantLandInspection, ["sectionMurn", "soilConditionMurn"]);
  }
  if (response.data.vacantLandInspection?.disturbanceFactorMurns) {
    await fillLookupsForListOfMurns(response.data.vacantLandInspection, "disturbanceFactorMurns");
  }

  if (response.data.vacantLandInspection?.measureImplementedMurns) {
    await fillLookupsForListOfMurns(response.data.vacantLandInspection, "measureImplementedMurns");
  }

  if (response.data?.dustControlInspection)
    await fillLookupValuesByPropertyNames(response.data.dustControlInspection, [
      "weatherMurn",
      "windSpeedMurn",
      "windGustMurn",
      "windDirectionMurn",
      "siteStatusMurn",
      "spokeWithTitle1Murn",
      "spokeWithMethod1Murn",
      "spokeWithMethod2Murn",
      "spokeWithTitle2Murn",
      "violationIn1000FtOfMurn",
      "fugitiveDustSourceMurn",
      "opacityTestMethodMurn",
      "projectSoilsMurn",
      "trackoutDeviceMurn",
      "mitigationEquipmentMurn",
      "soilCrustDeterminationMurn",
      "projectSizeMurn",
      "stagingParkingAreaMurn",
      "dcopSignMurn",
      "dcopOnSiteMurn",
      "ssPermitsMurn",
    ]);

  return response.data;
}

function requestAdapter(data: ComplaintInvestigation, preserveComplaints: boolean = false) {
  data.assignedTo = JSON.stringify(data.assignedToObj) == "{}" ? null : JSON.stringify(data.assignedToObj);
  data.reviewedBy = JSON.stringify(data.reviewedByObj) == "{}" ? null : JSON.stringify(data.reviewedByObj);

  if (preserveComplaints) {
    const complaints: any = [];
    data.complaints?.forEach((complaint) => {
      complaints.push({ id: complaint.id });
    });
    data.complaints = complaints;
  } else {
    delete data.complaints;
  }

  // vacantLandParcels are not a part of the investigation request object.
  delete data.vacantLandParcels;

  removeExtraVLInspectionFields(data);
  removeExtraDustInspectionFields(data);

  // Note: data.vacantLandInspection and data.dustControlInspection are conditionally removed on the BE based on the programTypeMurn.

  return data;
}

// Convert server data for use in the page's form (which may have groupings, etc different from the server data)
function toFormkit<T extends ComplaintInvestigation | ComplaintInvestigationNew>(data: T) {
  if (data.dustControlInspection) {
    data.dustControlInspection_0 = { ...data.dustControlInspection };
    data.dustControlInspection_1 = { ...data.dustControlInspection };
    data.dustControlInspection_2 = { ...data.dustControlInspection };
    data.dustControlInspection_3 = { ...data.dustControlInspection };
    data.dustControlInspection_4 = { ...data.dustControlInspection };
  }
  if (data.vacantLandInspection) {
    //Note - this gives all the fields to the seperated inspection pieces. We want to make sure when we combine it back, we take the fields from the right secions.
    data.vacantLandInspection_0 = { ...data.vacantLandInspection };
    data.vacantLandInspection_1 = { ...data.vacantLandInspection };
    data.vacantLandInspection_2 = { ...data.vacantLandInspection };
  }

  if (data.vacantLandInspection_0) murnAddressToFormkit(data.vacantLandInspection_0, "mail");
  // fixUtcDates(data);
}

// Convert formkit data to prep it for sending to the server (which is probably flatter than the fk data)
function fromFormkit(data: ComplaintInvestigation) {
  if (data.vacantLandParcelsCreate && data.vacantLandParcelIds) {
    data.vacantLandParcelsCreate = data.vacantLandParcelsCreate.filter((x) => data.vacantLandParcelIds?.includes(x.id));
  }
  data.vacantLandParcelsCreate?.forEach((x) => {
    murnAddressFromFormkit(x, "mail");
  });
  if (data.dustControlInspection) {
    combineSeperatedDustControlFields(data);
  }
  if (data.vacantLandInspection) {
    combineSeperatedVacantLandFields(data);
    murnAddressFromFormkit(data.vacantLandInspection, "mail");
  }
}
